import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { SESSION_CONFIG } from '../config/session';

/**
 * Hook to monitor user activity and update last activity timestamp
 * Tracks mouse movements, keyboard input, clicks, touches, and scrolling
 */
export const useInactivityMonitor = () => {
  const updateActivity = useAuthStore((state) => state.updateActivity);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity event handlers
    const handleActivity = () => {
      updateActivity();
    };

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity updates to avoid excessive calls
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledHandleActivity = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          handleActivity();
          throttleTimeout = null;
        }, SESSION_CONFIG.ACTIVITY_THROTTLE);
      }
    };

    // Attach event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledHandleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledHandleActivity);
      });
    };
  }, [isAuthenticated, updateActivity]);
};
